import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { userEndpoints } from './endpoints.js';
import { userItem, type UserFormInput, type UserItem } from './schemas.js';

/**
 * Writes for the Users module.
 *
 * Create is a single POST; update writes the profile, then replaces the
 * selected roles, then replaces the selected properties. All three are needed
 * because the service models them as separate PATCH surfaces.
 */
const WRITTEN_KEYS = [queryKeys.users.lists, queryKeys.users.details];

function createUserPayload(values: UserFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    email: values.email ?? '',
    password: values.password ?? '',
    roleIds: values.roleIds,
    propertyIds: values.propertyIds,
  };

  if (values.firstName !== '' && values.firstName !== undefined) {
    payload.firstName = values.firstName;
  }
  if (values.lastName !== '' && values.lastName !== undefined) {
    payload.lastName = values.lastName;
  }

  return payload;
}

function updateUserPayload(values: UserFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  if (values.status !== '' && values.status !== undefined) {
    payload.status = values.status;
  }
  payload.firstName =
    values.firstName === '' || values.firstName === undefined ? null : values.firstName;
  payload.lastName =
    values.lastName === '' || values.lastName === undefined ? null : values.lastName;

  return payload;
}

export function useCreateUser(): UseMutationResult<UserItem, unknown, UserFormInput> {
  return useApiMutation<UserFormInput, UserItem>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        userEndpoints.create,
        createUserPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(userItem, payload, userEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateUserVariables {
  readonly userId: string;
  readonly values: UserFormInput;
}

export function useUpdateUser(): UseMutationResult<unknown, unknown, UpdateUserVariables> {
  return useApiMutation<UpdateUserVariables, unknown>({
    mutationFn: async (api, { userId, values }) => {
      await api.patch<unknown>(userEndpoints.update(userId), updateUserPayload(values));
      await api.patch<unknown>(userEndpoints.roles(userId), { roleIds: values.roleIds });
      await api.patch<unknown>(userEndpoints.properties(userId), {
        propertyIds: values.propertyIds,
      });
    },
    invalidate: WRITTEN_KEYS,
  });
}

export function useSoftDeleteUser(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, userId) => {
      await api.delete<unknown>(userEndpoints.delete(userId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
